import React, { FC, useState, useEffect, useLayoutEffect, useRef } from "react";
import { useMachine } from "@xstate/react";
import Alert from "./Alert";
import { NotificationIntent } from "types/Notification";
import { transititionMachine } from "./transition.machine";

function useTransition(isShown: boolean) {
  const isMounted = useRef(false);
  const [current, send] = useMachine(transititionMachine);

  useEffect(() => {
    if (isShown && current.matches("exited") && !isMounted.current) {
      send({ type: "enter" });
    }

    if (!isShown && (current.matches("entered") || (current as any).matches("entering"))) {
      send({ type: "exit" });
    }

    isMounted.current = true;
  }, [isShown]);

  return current.value;
}

const Toast: FC<
  React.PropsWithChildren<{
    title: string;
    intent: NotificationIntent;
    duration: number;
    onRemove(): void;
  }>
> = ({ title, intent, duration = 2, onRemove, children }) => {
  const [isShown, setShown] = useState(true);

  const state = useTransition(isShown);

  useEffect(() => {
    if (!isShown && state === "exited") {
      onRemove();
    }
  }, [state]);

  const ref = useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    if (ref.current === null) return;

    const { height } = ref.current.getBoundingClientRect();
    setHeight(height);
  }, [state]);

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function clearTimer() {
    timer.current && clearTimeout(timer.current);
  }

  function startTimer() {
    timer.current = setTimeout(() => {
      close();
    }, duration * 1000);
  }

  function close() {
    clearTimer();
    setShown(false);
  }

  useEffect(() => {
    if (!localStorage.getItem("debug::notification")) {
      startTimer();
    }
    return () => {
      clearTimer();
    };
  }, []);

  function onMouseEnter() {
    clearTimer();
  }

  function onMouseLeave() {
    startTimer();
  }

  if (state === "exited") {
    return null;
  }

  return (
    <div
      data-state={state}
      className="toast"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        height: height,
        zIndex: 150,
        marginBottom: isShown ? 0 : -height,
      }}
    >
      <div ref={ref} style={{ padding: 8 }}>
        <Alert intent={intent} title={title} onRemove={() => close()}>
          {children}
        </Alert>
      </div>
    </div>
  );
};

export default Toast;
